import { Result } from '../models/result.model';

export interface CapacidadeNavegacao {
  auditoria: boolean;
}

export interface CapacidadeEntrada {
  planilhas: boolean;
  contratacoes: boolean;
  auditoria: boolean;
}

export type ModuloAcesso = keyof CapacidadeEntrada;

export interface EntradaAcessoResponse {
  resultado: 'permitido' | 'negado' | 'expirado';
  usuarioReferencia: string | null;
  unidadeReferencia: string | null;
  diretoriaReferencia: string | null;
  /** Nomes para exibição no cabeçalho; ausentes quando o cadastro não os possui. */
  nomeUsuario?: string | null;
  nomeUnidade?: string | null;
  avisoValidade: string | null;
  /** JWT emitido na entrada permitida; fica em memória e nunca faz parte do contexto exposto (spec 001, Q11). */
  token: string | null;
  capacidadeNavegacao: CapacidadeNavegacao | null;
  capacidadeEntrada: CapacidadeEntrada | null;
}

export interface ContextoAcesso extends Omit<EntradaAcessoResponse, 'token'> {
  resultado: 'permitido';
  usuarioReferencia: string;
  unidadeReferencia: string;
  capacidadeNavegacao: CapacidadeNavegacao;
  capacidadeEntrada: CapacidadeEntrada;
}

export type EntradaPermitida = ContextoAcesso & { token: string };

export interface EntradaCredenciaisRequest {
  matricula: string;
  senha: string;
}

const CAMPOS_NAVEGACAO: (keyof CapacidadeNavegacao)[] = ['auditoria'];

function textoOpcional(valor: unknown): boolean {
  return valor === undefined || valor === null || typeof valor === 'string';
}

function objeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

/** Verifica o envelope e o contexto antes de reconhecer uma admissão no cliente. */
export function entradaPermitida(valor: unknown): valor is Result<EntradaPermitida> & { data: EntradaPermitida } {
  if (!objeto(valor) || valor['isSuccess'] !== true || typeof valor['message'] !== 'string'
    || !Array.isArray(valor['errors']) || valor['errors'].length !== 0) return false;
  const dados = valor['data'];
  if (!objeto(dados) || dados['resultado'] !== 'permitido'
    || typeof dados['usuarioReferencia'] !== 'string' || !dados['usuarioReferencia'].trim()
    || typeof dados['unidadeReferencia'] !== 'string' || !dados['unidadeReferencia'].trim()
    || !(dados['diretoriaReferencia'] === null || typeof dados['diretoriaReferencia'] === 'string')
    || !textoOpcional(dados['nomeUsuario']) || !textoOpcional(dados['nomeUnidade'])
    || !(dados['avisoValidade'] === null || typeof dados['avisoValidade'] === 'string')
    || typeof dados['token'] !== 'string' || !dados['token'].trim()) return false;
  const navegacao = dados['capacidadeNavegacao'];
  const entrada = dados['capacidadeEntrada'];
  return objeto(navegacao) && objeto(entrada)
    && CAMPOS_NAVEGACAO.every(campo => typeof navegacao[campo] === 'boolean' && typeof entrada[campo] === 'boolean')
    && typeof entrada['planilhas'] === 'boolean' && typeof entrada['contratacoes'] === 'boolean';
}

/** Recusas de negócio são diferentes de ProblemDetails e nunca fornecem contexto. */
export function entradaRecusada(valor: unknown): valor is Result<EntradaAcessoResponse> & { data: EntradaAcessoResponse } {
  if (!objeto(valor) || valor['isSuccess'] !== false || typeof valor['message'] !== 'string'
    || !Array.isArray(valor['errors']) || !valor['errors'].every(item => typeof item === 'string')) return false;
  const dados = valor['data'];
  return objeto(dados) && (dados['resultado'] === 'negado' || dados['resultado'] === 'expirado')
    && ['usuarioReferencia', 'unidadeReferencia', 'diretoriaReferencia', 'avisoValidade', 'token',
      'capacidadeNavegacao', 'capacidadeEntrada'].every(campo => dados[campo] === null);
}
